/**
 * ضحا قصه — Design System (Midnight Library)
 * ─────────────────────────────────────────────────────────────────
 * THE source of truth for design tokens. This file is IMPORTED BY THE APP —
 * `miniapp/theme.js` and `admin/theme.js` are gone (2026-09-15); every screen and
 * `squircle.js` import straight from here, so editing a value here really does
 * change it everywhere. Keep it that way: if you add a token, import it, don't
 * copy it.
 *
 *   import { C, FONT } from "../design-system.js";
 *
 * The GLOBAL CSS lives here too, as THREE constants — `BASE_CSS` plus
 * `MINIAPP_CSS` / `ADMIN_CSS` (bottom of this file). The two surfaces genuinely
 * differ (admin `body` is `C.bg` with 8px scrollbars — a desktop panel; the Mini
 * App is `C.bgDeep` with none — a phone WebView), which is why ONE blob cannot
 * work. But that argues for a per-surface constant, not for exiling the CSS to
 * the shells: splitting it across two shell files is exactly how `body` AND
 * `@keyframes popIn` quietly drifted apart. Side by side, a difference is
 * reviewable; in two files it is invisible.
 *
 * Components are not here either — they live in `admin/ui.jsx` / `miniapp/ui.jsx`
 * as real components (`Btn`, `Tabs`, `BarChart`, `Stars`). A bag of inline style
 * objects is what the component-first rule exists to prevent.
 * ─────────────────────────────────────────────────────────────────
 */

// ── Color Palette ─────────────────────────────────────────────────────────────
export const C = {
  // Backgrounds — darkest to lightest
  bgDeep:    "#090D12",   // page background for full-bleed dark screens
  bg:        "#0D1117",   // default app background
  bgCard:    "#161B22",   // cards, panels, modals
  bgHover:   "#1C2330",   // hover state on interactive cards

  // Gold — primary accent (warmth, storybook feel)
  gold:      "#C9973A",
  goldLight: "#E8B96A",   // gradient end, highlights
  goldDim:   "#7A5A1E",   // subtle gold backgrounds, borders

  // Cream — primary text on dark
  cream:     "#F0E6D3",
  creamDim:  "#A89880",   // secondary text, placeholders

  // Teal — success, active states, new badges
  teal:      "#2DD4BF",
  tealDim:   "#134E4A",   // subtle teal backgrounds

  // Purple — questionnaire accent, secondary highlights
  purple:    "#A78BFA",

  // Red — errors, warnings, high-severity flags
  red:       "#F87171",

  // Neutrals
  textMid:   "#8B8070",   // tertiary text, labels
  textDim:   "#7A7166",   // disabled text, hints (was #4A4540 — too dark to read, 2026-10-27)
  border:    "#1E2530",   // default border
  borderGold:"#3A2E18",   // gold-tinted border for highlighted cards
};

// ── Storybook palette — the tale library and its cards ────────────────────────
// The tale surfaces are lit like a picture book at night (lantern gold on deep
// blue), not like the parent UI, so they get their own named colours — but they
// are still TOKENS: no screen writes a hex of its own (product owner, 2026-10-27).
export const TALE = {
  night:     "#0B1030",   // the card / sky behind a tale
  nightInk:  "#050819",   // the shade at the bottom of a card
  lantern:   "#FFD66B",   // play button, twinkles' glow
  lanternDeep:"#FFB648",  // play button gradient end
  ember:     "#FFC86E",   // the lantern's halo
  spark:     "#FFF2C0",   // a twinkling star
  ink:       "#2A1B08",   // dark text on lantern gold
  label:     "#FFE3A3",   // chips on the art
  title:     "#FFF4DD",   // a tale's title on its card
  sub:       "#D9CCB4",   // its subtitle
  tealInk:   "#062A26",   // text on a teal «رایگان» chip
};

// Other companies' colours — messengers and payment gateways — exactly as their
// official marks use them (never "our" versions of them).
export const BRAND = {
  bale:         "#00B894",   // Bale's mark
  baleWallet:   "#00B37E",   // Bale wallet payments
  telegram:     "#2AABEE",
  telegramDeep: "#229ED9",   // the telegram mark's gradient end
  eitaa:        "#EE7F22",
  soroush:      "#196FF0",
  digipay:      "#1159FC",
  snapp:        "#007DFA",   // SnappPay
  zarinpal:     "#F5B800",
};

// The games screen's sky, while its lazy chunk loads.
export const GAME_SKY = "#1B1446";

// Plain white / black, for the few places a colour must be neutral (shadows,
// text on a saturated brand colour).
export const WHITE = "#FFFFFF";
export const BLACK = "#000000";

// `alpha(C.gold, .2)` → "#C9973A33". 8-digit hex is valid CSS everywhere we run,
// so a translucent colour stays a reference to its token instead of a new rgba().
export const alpha = (hex, a) => `${hex}${Math.round(Math.max(0, Math.min(1, a)) * 255).toString(16).padStart(2, "0")}`;

// ── Semantic Color Aliases ────────────────────────────────────────────────────
export const STATUS_COLORS = {
  active:   { color: C.teal,    bg: `${C.teal}22`,    border: `${C.teal}44`,    label: "فعال"      },
  pending:  { color: C.gold,    bg: `${C.gold}22`,    border: `${C.gold}44`,    label: "در انتظار" },
  expired:  { color: C.textMid, bg: `${C.textMid}22`, border: `${C.textMid}44`, label: "منقضی"     },
  error:    { color: C.red,     bg: `${C.red}22`,     border: `${C.red}44`,     label: "خطا"       },
  flagged:  { color: C.red,     bg: `${C.red}11`,     border: `${C.red}44`,     label: "نیازمند توجه" },
};

export const SECTION_COLORS = {
  temperament: C.gold,      // بخش ۱ پرسشنامه
  fears:       C.purple,    // بخش ۲ پرسشنامه
  hero:        C.teal,      // بخش ۳ پرسشنامه
};

// ── Gradients ─────────────────────────────────────────────────────────────────
export const GRADIENT = {
  gold:     `linear-gradient(135deg, ${C.gold}, ${C.goldLight})`,
  goldSoft: `linear-gradient(135deg, ${C.gold}22, ${C.gold}11)`,
  header:   `linear-gradient(180deg, ${C.bgDeep} 0%, ${C.bg} 100%)`,
  glow:     `radial-gradient(circle, ${C.goldDim}55 0%, transparent 70%)`,
};

// ── Typography ────────────────────────────────────────────────────────────────
export const FONT = "'Estedad', sans-serif";

// Font sizes — all +2px from standard to aid readability on mobile
export const FS = {
  xs:    13,   // labels, captions, timestamps
  sm:    14,   // secondary body, table cells
  base:  15,   // primary body, list items
  md:    16,   // emphasized body, input text
  lg:    17,   // section titles, button text
  xl:    20,   // screen subtitles
  "2xl": 22,   // screen titles
  "3xl": 24,   // page headings
  "4xl": 28,   // hero numbers (stats)
  "5xl": 30,   // logo/brand name
};

// ── Border Radius ─────────────────────────────────────────────────────────────
// NOTE: corners app-wide are re-rendered as SUPERELLIPSE squircles at runtime by
// `squircle.js` (see SQUIRCLE below). The px values here decide only whether an
// element opts in (any uniform radius > 0) — the actual shape comes from the token
// for its type. Chips/badges use `full` so they render as true pills.
export const R = {
  chip: 9999, // badges, tags, status chips → fully-rounded pills
  md:   12,   // inputs, small buttons, table cells
  lg:   14,   // buttons, items in lists
  xl:   16,   // action buttons, story cards
  "2xl":18,   // subscription plan cards
  "3xl":20,   // main cards, panels
  full: 9999, // pills, avatar circles
};

// ── Squircle corner tokens (the runtime engine — squircle.js) ─────────────────
// n = smoothness (superellipse exponent; higher = squarer sides + tighter corner),
// r = roundness (px). Shape is chosen by element TYPE so every button is identical
// and every container is identical. Change here → the whole app updates.
export const SQUIRCLE = {
  container: { n: 5, r: 42 },   // cards, panels, rows, modals
  button:    { n: 3, r: 36 },   // buttons, tabs (softer superellipse, rounder)
  // pills (chips, circles) are left native fully-rounded, not squircled.
};

// ── Shadows ───────────────────────────────────────────────────────────────────
export const SHADOW = {
  gold:    `0 4px 24px ${C.gold}44`,
  goldSm:  `0 4px 20px ${C.gold}33`,
  teal:    `0 0 16px ${C.teal}22`,
  card:    `0 1px 8px rgba(0,0,0,0.3)`,
  glow:    `0 0 60px ${C.gold}22, 0 0 120px ${C.gold}11`,
};

// ── Spacing ───────────────────────────────────────────────────────────────────
export const SPACE = {
  xs:  4,
  sm:  8,
  md:  12,
  lg:  16,
  xl:  20,
  "2xl": 24,
  "3xl": 28,
  "4xl": 32,
  page: 20,          // horizontal page padding (mobile)
  pageDesktop: 40,   // horizontal page padding (desktop admin)
};

// ── Global CSS ────────────────────────────────────────────────────────────────
// The shells USED to own these outright, which is how `body` and `@keyframes
// popIn` quietly drifted apart between the two surfaces without anyone noticing.
// They live here now so every design decision is in one file — but as THREE
// constants, not one: a single blob could only ever be right for one surface.
//
// Each shell injects `BASE_CSS + its own`. The split is mechanical: a rule whose
// body was byte-identical in both shells is BASE; anything present in only one,
// or present in both with different bodies, stays with its surface. That is what
// makes the split behaviour-preserving.
//
// NOT here: `FIRST_RUN_CSS` (exported from `miniapp/faces.jsx`). That one is
// deliberately component-owned so a probe page can render byte-identical rules
// for the 3D face sphere — see CLAUDE.md.

/** Shared by the Mini App and the admin panel — fonts, reset, press/ripple/spark. */
export const BASE_CSS = `
/* The app is in the background → every animation waits (device.js). */
html.zg-bg *, html.zg-bg *::before, html.zg-bg *::after { animation-play-state: paused !important; }
/* Self-hosted Estedad — Google Fonts is blocked in Iran, so we serve the
         woff2 files locally from /fonts (no system-font fallback). */
@font-face { font-family:'Estedad'; font-weight:400; font-display:swap; src:url('/fonts/Estedad-Regular.woff2') format('woff2'); }
@font-face { font-family:'Estedad'; font-weight:600; font-display:swap; src:url('/fonts/Estedad-SemiBold.woff2') format('woff2'); }
@font-face { font-family:'Estedad'; font-weight:700; font-display:swap; src:url('/fonts/Estedad-Bold.woff2') format('woff2'); }
@font-face { font-family:'Estedad'; font-weight:800; font-display:swap; src:url('/fonts/Estedad-ExtraBold.woff2') format('woff2'); }
* { margin:0; padding:0; box-sizing:border-box; -webkit-tap-highlight-color:transparent; }
input, button, textarea, select { font-family: inherit; }
/* ── Interaction & entrance ── */
/* Unified 2026-09-15. The two shells had drifted to scale(0.8) (Mini App) and
   scale(0.92) (admin). 0.92 wins because 3 of the 5 call sites are SURFACES
   (modals/cards, one 640px wide) where 0.8 is ~128px of travel and reads as
   jumpy — that is why the admin softened it. The other two are 44px avatar
   tiles, where the difference is ~5px and still reads as a pop. If those
   tiles ever need their bounce back, add a SECOND named keyframe rather than
   re-splitting this one per surface. */
@keyframes popIn { from{opacity:0;transform:scale(0.92)} to{opacity:1;transform:scale(1)} }
@keyframes fadeIn   { from{opacity:0} to{opacity:1} }
@keyframes magspin  { to { transform: rotate(-360deg); } }
@keyframes magfly { 0%{opacity:0;transform:translate(0,0) scale(1)} 3%{opacity:1} 46%{opacity:0;transform:translate(var(--jx,0px),var(--fly,-12px)) scale(0.3)} 100%{opacity:0;transform:translate(var(--jx,0px),var(--fly,-12px)) scale(0.3)} }
/* ── Press feedback — calm by default ── */
.press {
        position: relative;
        transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1), filter 0.12s ease;
        -webkit-tap-highlight-color: transparent;
        transform-origin: center;
      }
.press::after {
        content: "";
        position: absolute;
        inset: 0;
        border-radius: inherit;
        background: radial-gradient(circle at center, ${C.cream}26 0%, ${C.gold}14 45%, transparent 74%);
        opacity: 0;
        transform: scale(0.5);
        transition: opacity 0.35s ease, transform 0.35s ease;
        pointer-events: none;
      }
.press:active {
        transform: scale(0.94);
        filter: brightness(1.15);
        transition: transform 0.08s ease, filter 0.08s ease;
      }
.press:active::after {
        opacity: 1;
        transform: scale(1.05);
        transition: opacity 0.05s ease, transform 0.05s ease;
      }
/* ── Celebratory buttons — full game juice (subscribe, pay, finish) ── */
.celebrate:active {
        transform: scale(0.9) translateY(2px);
        filter: brightness(1.3) saturate(1.25) !important;
        box-shadow: 0 0 50px ${C.gold}cc, 0 0 90px ${C.gold}55 !important;
      }
.celebrate::after {
        background: radial-gradient(circle at center, ${C.cream}55 0%, ${C.gold}33 40%, transparent 75%) !important;
        mix-blend-mode: screen;
      }
/* ── JS-injected ripple ring (one per tap, all buttons) ── */
.ripple-ring {
        position: absolute;
        border-radius: 50%;
        border: 2px solid ${C.goldLight};
        pointer-events: none;
        transform: translate(-50%, -50%) scale(0);
        animation: rippleRing 0.55s cubic-bezier(0.22, 0.8, 0.3, 1) forwards;
        z-index: 5;
      }
@keyframes rippleRing {
        0%   { transform: translate(-50%,-50%) scale(0);   opacity: 0.7; border-width: 2px; }
        100% { transform: translate(-50%,-50%) scale(1);   opacity: 0;   border-width: 0px; }
      }
/* ── JS-injected spark particles (celebrate buttons only) ── */
.spark {
        position: fixed;
        width: 8px; height: 8px;
        border-radius: 50%;
        pointer-events: none;
        z-index: 9999;
        animation: sparkFly 0.55s ease-out forwards;
      }
@keyframes sparkFly {
        0%   { transform: translate(0,0) scale(1);   opacity: 1; }
        100% { transform: translate(var(--dx), var(--dy)) scale(0); opacity: 0; }
      }
`;

/** Mini App only — host safe-area insets, full-bleed screens, emoji animations. */
export const MINIAPP_CSS = `
/* direction is set on <html> by the language provider (rtl for fa, ltr for en). */
body { background:${C.bgDeep}; font-family: 'Estedad', sans-serif; }
/* Squircle corners are handled by the runtime engine in src/squircle.js
         (per-element clip-path + SVG border stroke — borders follow the curve). */
/* ── Host fullscreen safe areas (status bar / messenger buttons / Android nav) ── */
:root { --tg-safe-top: 0px; --tg-safe-bottom: 0px; }
.app-root { padding-top: var(--tg-safe-top); padding-bottom: var(--tg-safe-bottom); }
/* Keep the active screen exactly one viewport tall once the insets are reserved,
         so content fills the visible area without sliding under Telegram/OS UI. */
.app-root > div:not(.auth-error) { min-height: calc(100dvh - var(--tg-safe-top) - var(--tg-safe-bottom)) !important; }
/* ── Full-bleed screens ──
         Padding the WRAPPER reserves the inset OUTSIDE the child's background, so the
         page shows through as a dark bar above the artwork (reported on Telegram
         fullscreen AND in the Eitaa mini app). A full-bleed screen cancels that
         padding and reserves the inset INSIDE itself instead, so its own background
         and decorative layers reach the top of the display while only the CONTENT is
         pushed clear of the status bar. Used by onboarding. */
.app-root--bleed { padding-top: 0; padding-bottom: 0; }
.app-root--bleed > div:not(.auth-error) { min-height: 100dvh !important; }
/* ── Ambient emoji animations ── */
@keyframes twinkle  { 0%,100%{opacity:0.1} 50%{opacity:0.5} }
@keyframes float    { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-6px)} }
@keyframes floatBig { 0%,100%{transform:translateY(0) rotate(-3deg)} 50%{transform:translateY(-10px) rotate(3deg)} }
@keyframes wiggle   { 0%,100%{transform:rotate(-8deg)} 50%{transform:rotate(8deg)} }
@keyframes bounce   { 0%,100%{transform:translateY(0)} 25%{transform:translateY(-12px)} 50%{transform:translateY(0)} 75%{transform:translateY(-5px)} }
@keyframes breathe  { 0%,100%{transform:scale(1)} 50%{transform:scale(1.06)} }
@keyframes slideUp  { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
@keyframes pulse    { 0%,100%{transform:scale(1);opacity:0.6} 50%{transform:scale(1.1);opacity:0.2} }
@keyframes pulse2   { 0%,100%{box-shadow:0 0 0 0 ${C.purple}44} 50%{box-shadow:0 0 0 8px ${C.purple}00} }
@keyframes pulseDot { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.5;transform:scale(0.85)} }
@keyframes confettiFall { to{transform:translateY(105vh) rotate(720deg);opacity:0} }
.cardHover:hover { border-color:${C.gold}55 !important; transform: translateY(-2px); }
.emojiPick:active { transform: scale(0.82); filter: brightness(1.2); }
.editTap:active { background: ${C.bgHover} !important; }
.playBtn:active { transform: scale(0.9); filter: brightness(1.15); }
.glow { animation: glowPulse 2.5s ease-in-out infinite; }
@keyframes glowPulse { 0%,100%{box-shadow:0 4px 24px ${C.gold}44} 50%{box-shadow:0 4px 40px ${C.gold}99} }
}

      ::-webkit-scrollbar { width:0; height:0; }
      input,textarea,select { outline:none; }
`;

/** Admin panel only — desktop scrollbars, calendar/toast animations. */
export const ADMIN_CSS = `
body { background:${C.bg}; direction:rtl; font-family: 'Estedad', sans-serif; }
@keyframes spin   { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
@keyframes calNext { from { opacity:0; transform: translateX(-26px); } to { opacity:1; transform:none; } }
@keyframes calPrev { from { opacity:0; transform: translateX(26px); } to { opacity:1; transform:none; } }
@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
@keyframes fadeSlideIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
@keyframes toastIn  { from { opacity: 0; transform: translateY(-14px) scale(0.97); } to { opacity: 1; transform: translateY(0) scale(1); } }
@keyframes toastOut { from { opacity: 1; transform: translateY(0) scale(1); } to { opacity: 0; transform: translateY(-10px) scale(0.97); } }
::-webkit-scrollbar { width:8px; height:8px; }
::-webkit-scrollbar-thumb { background:${C.border}; border-radius:8px; }
`;
